import React from 'react';
import { useTranslation } from 'react-i18next';
import { RefreshCw, X } from 'lucide-react';
import { useDailyBrief } from '../hooks/useDailyBrief';
import type { WeatherData, EventWeatherData, CalendarEventBrief, TaskBrief } from '../hooks/useDailyBrief';

interface DailyBriefPanelProps {
  uid: string | null;
  isOpen: boolean;
  onClose: () => void;
  onRefresh: () => Promise<void>;
  isRefreshing: boolean;
}

function getWeatherEmoji(icon: string): string {
  const prefix = icon.slice(0, 2);
  const map: Record<string, string> = {
    '01': '☀️',
    '02': '⛅',
    '03': '☁️',
    '04': '☁️',
    '09': '🌧️',
    '10': '🌧️',
    '11': '⛈️',
    '13': '❄️',
    '50': '🌫️',
  };
  return map[prefix] ?? '🌤️';
}

function formatTime(iso: string): string {
  try {
    const date = new Date(iso);
    return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  } catch {
    return iso;
  }
}

function WeatherCard({ weather, prefix }: { weather: WeatherData; prefix?: string }) {
  return (
    <div className="flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-800 rounded-2xl">
      <span className="text-2xl">{getWeatherEmoji(weather.icon)}</span>
      <div>
        {prefix && <p className="text-2xs font-black text-slate-400 uppercase tracking-widest">{prefix}</p>}
        <p className="text-sm font-bold text-slate-900 dark:text-white">{weather.location}</p>
        <p className="text-xs text-slate-500">{Math.round(weather.temp)}°C — {weather.description}</p>
      </div>
    </div>
  );
}

export const DailyBriefPanel: React.FC<DailyBriefPanelProps> = ({
  uid,
  isOpen,
  onClose,
  onRefresh,
  isRefreshing,
}) => {
  const { t } = useTranslation();
  const { brief, loading } = useDailyBrief(uid);

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black/40 z-[199] transition-opacity duration-300 ${isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
        onClick={onClose}
      />

      {/* Panel */}
      <div
        className={`fixed inset-y-0 right-0 w-full sm:w-96 bg-white dark:bg-slate-900 shadow-2xl z-[200] flex flex-col transform transition-transform duration-300 ease-in-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-200 dark:border-slate-800 flex-shrink-0">
          <div>
            <h2 className="text-xl font-black text-slate-900 dark:text-white">{t('brief.title', 'Daily Brief')}</h2>
            {brief && <p className="text-xs text-slate-400 mt-0.5">{brief.date}</p>}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onRefresh}
              disabled={isRefreshing}
              className="p-2 rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-500 hover:text-brand disabled:opacity-50 transition-colors"
              title={t('brief.refresh', 'Refresh brief')}
            >
              <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={onClose}
              className="p-2 rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-500 hover:text-red-500 transition-colors"
              title={t('common.close', 'Close')}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto">
          <div className="p-6 space-y-4">
            {loading && (
              <>
                <div className="bg-slate-200 dark:bg-slate-800 animate-pulse rounded-2xl h-24" />
                <div className="bg-slate-200 dark:bg-slate-800 animate-pulse rounded-2xl h-16" />
                <div className="bg-slate-200 dark:bg-slate-800 animate-pulse rounded-2xl h-20" />
              </>
            )}

            {!loading && !brief && (
              <p className="text-center text-slate-400 text-sm py-12">
                {t('brief.empty', 'No brief yet. Click refresh to generate.')}
              </p>
            )}

            {!loading && brief && (
              <>
                {/* Narrative */}
                {brief.narrative && (
                  <div className="bg-indigo-50 dark:bg-indigo-900/20 rounded-2xl p-4">
                    <p className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed">
                      {brief.narrative}
                    </p>
                  </div>
                )}


                {/* Stoic Quote */}
                {(brief as any).stoicQuote && (
                  <div className="border-l-2 border-brand pl-3 py-1 bg-slate-50 dark:bg-slate-800/50 rounded-r-2xl">
                    <p className="text-xs text-slate-500 dark:text-slate-400 italic leading-relaxed">&ldquo;{(brief as any).stoicQuote.text}&rdquo;</p>
                    <p className="text-2xs font-black text-slate-400 mt-1">— {(brief as any).stoicQuote.author}</p>
                  </div>
                )}
                {/* Weather */}
                {(brief.homeWeather || brief.eventWeather?.length > 0) && (
                  <div className="space-y-2">
                    <h3 className="font-black text-xs uppercase tracking-widest text-slate-400">{t('brief.weather', 'Weather')}</h3>
                    {brief.homeWeather && (
                      <WeatherCard weather={brief.homeWeather} prefix={t('brief.weather_home', 'Home')} />
                    )}
                    {brief.eventWeather?.map((ew: EventWeatherData, i: number) => (
                      <WeatherCard key={i} weather={ew} prefix={ew.eventTitle} />
                    ))}
                  </div>
                )}

                {/* Today's Events */}
                {brief.events?.length > 0 && (
                  <div className="space-y-2">
                    <h3 className="font-black text-xs uppercase tracking-widest text-slate-400">{t('brief.todays_events', "Today's Events")}</h3>
                    {brief.events.map((event: CalendarEventBrief) => (
                      <div key={event.eventId} className="flex items-start gap-3 p-3 bg-slate-50 dark:bg-slate-800 rounded-2xl">
                        <span className="text-lg">📅</span>
                        <div>
                          <p className="text-sm font-bold text-slate-900 dark:text-white">{event.title}</p>
                          <p className="text-xs text-slate-400">{formatTime(event.start)}{event.location ? ` · ${event.location}` : ''}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Your Tasks */}
                {brief.tasks?.length > 0 && (
                  <div className="space-y-2">
                    <h3 className="font-black text-xs uppercase tracking-widest text-slate-400">{t('brief.your_tasks', 'Your Tasks')}</h3>
                    {brief.tasks.map((task: TaskBrief) => (
                      <div key={task.taskId} className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-2xl">
                        <p className="text-sm font-bold text-slate-900 dark:text-white">{task.title}</p>
                        <span className={`text-2xs font-black uppercase px-2 py-0.5 rounded-full ${
                          task.status === 'in_progress' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' : 'bg-slate-200 text-slate-600 dark:bg-slate-700 dark:text-slate-400'
                        }`}>
                          {task.status.replace('_', ' ')}
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Suggested Tasks */}
                {brief.suggestions?.length > 0 && (
                  <div className="space-y-2">
                    <h3 className="font-black text-xs uppercase tracking-widest text-slate-400">{t('brief.free_time', 'You Have Free Time — Consider:')}</h3>
                    {brief.suggestions.map((task: TaskBrief) => (
                      <div key={task.taskId} className="p-3 bg-slate-50 dark:bg-slate-800/50 rounded-2xl border border-dashed border-slate-200 dark:border-slate-700">
                        <p className="text-sm text-slate-500 dark:text-slate-400">{task.title}</p>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};
